import { SPACE_ICONS, type SpaceIconDef } from "./space-icons.data.ts";
import { cn } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  空间图标 —— 两种画法，后台挑
 * ═══════════════════════════════════════════════════════════════
 *
 * 首页「按你家的空间挑」那一排，每一格的图标可以是：
 *
 * - **矢量图**（`sofa`）—— 站内自带的 36 个，装修建材主题，
 *   线条跟站里其他图标同一套，颜色跟着配色走。
 * - **emoji**（`emoji:🛋️`）—— 最早那一版的样子。
 *
 * 🔴 **两者不是"新旧"关系，是两种审美，各有各的代价：**
 *
 *   | | 矢量图 | emoji |
 *   |---|---|---|
 *   | 各设备是否一致 | 是 | **否** —— 字形由系统字体决定，同一个 🛋️ 在 iOS 是写实彩色沙发、Windows 是另一种画风、部分安卓上是方框 |
 *   | 跟不跟配色 | 跟 | **不跟** —— 自带颜色，换主题时它纹丝不动 |
 *   | 观感 | 克制、统一 | 活泼、一眼认得出 |
 *
 *   所以 emoji 不是"做错了"，是另一种选择 —— 但选它之前要知道上面那两条。
 *   后台那一页把这两句写在切换按钮旁边。
 *
 * 🔴 **图标数据在仓库里，不引任何外部图标脚本。**
 *    做过一版"后台填 iconfont 项目地址"的，去掉了 —— 它把首页的完整性挂在
 *    一个外部服务和一次配置上：地址没填 / 填错 / 对方改了编号，首页就缺一块，
 *    而看到的人只会以为页面坏了。图标是骨架，不是内容。
 */

const BY_KEY = new Map<string, SpaceIconDef>(SPACE_ICONS.map((x) => [x.key, x]));

export const EMOJI_PREFIX = "emoji:";

/**
 * 把存进配置里的值规整成图标 key。
 *
 * ⚠️ 兼容 `builtin:sofa` 这种老写法 —— 早先图标有两个来源（站内 / iconfont），
 *    值上带着前缀。iconfont 那条路已经去掉了，但库里存着的老值还在，
 *    不认的话老板配好的图标会集体变成占位图。
 * ⚠️ **`emoji:` 前缀不剥** —— 那是有效的一种画法，不是历史包袱。
 */
export function normalizeIconKey(icon: string): string {
  return icon.startsWith("builtin:") ? icon.slice("builtin:".length) : icon;
}

/** 这个值是不是 emoji 画法。 */
export function isEmojiIcon(icon: string): boolean {
  return icon.startsWith(EMOJI_PREFIX);
}

/** 取一个图标 key 对应的 emoji（用于「整排换成 emoji」）。认不出就给个房子。 */
export function emojiOf(icon: string): string {
  if (isEmojiIcon(icon)) return icon.slice(EMOJI_PREFIX.length);
  return BY_KEY.get(normalizeIconKey(icon))?.emoji ?? "🏠";
}

/** 按组分好的图标，给后台的选择器用。 */
export const ICON_GROUPS: { group: string; icons: SpaceIconDef[] }[] = [
  ...new Set(SPACE_ICONS.map((x) => x.group)),
].map((group) => ({ group, icons: SPACE_ICONS.filter((x) => x.group === group) }));

export interface SpaceIconProps {
  /** 图标 key（`sofa`）或 emoji（`emoji:🛋️`）。 */
  icon: string;
  /**
   * 边长（px）。
   *
   * 🔴 **必须显式给数字，不能只靠 `size-6` 这类 class。**
   *    emoji 是**文字**不是图形 —— class 设的是盒子的宽高，字号却还是继承来的
   *    14px，于是一个 44px 的圆底板里蹲着一个小得看不清的 emoji。
   *    这里把边长同时用作 svg 的宽高和 emoji 的字号，两种画法才一样大。
   */
  size: number;
  className?: string;
}

/**
 * 画一个空间图标。
 *
 * ⚠️ 颜色：矢量图用 `currentColor`（外面给 `text-white` 就是白的）；
 *    **emoji 给不了颜色**，它自带。这是它的性质，见文件头那张表。
 */
export function SpaceIcon({ icon, size, className }: SpaceIconProps) {
  if (isEmojiIcon(icon)) {
    const ch = icon.slice(EMOJI_PREFIX.length);
    return (
      <span
        className={cn("inline-flex shrink-0 items-center justify-center leading-none", className)}
        /** ⚠️ 字号略小于盒子：emoji 字形普遍带一点内边距，等宽会显得比矢量图大一圈。 */
        style={{ width: size, height: size, fontSize: Math.round(size * 0.92) }}
        aria-hidden
      >
        {ch}
      </span>
    );
  }

  /** ⚠️ 认不出就退回「全屋」那个图标，不留空白 —— 首页缺一块没人看得懂。 */
  const def = BY_KEY.get(normalizeIconKey(icon)) ?? BY_KEY.get("wholehouse");
  if (!def) return null;

  return (
    <svg
      className={cn("shrink-0", className)}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      {def.d.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
